import { useState, useEffect, useCallback } from "react";
import API, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { House, NavigationArrow, CarSimple, MapPin, Warning, MapTrifold } from "@phosphor-icons/react";

import DateInput from "./DateInput";
function fmtDrive(mins) {
  return mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
}

export default function OperativeRouteDialog({ open, onOpenChange, operative, initialDate }) {
  const [date, setDate] = useState(initialDate);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (open) setDate(initialDate); }, [open, initialDate]);

  const load = useCallback(async (d) => {
    if (!operative?.id || !d) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await API.get(`/in-house/route-plan/${operative.id}`, { params: { date: d } });
      setPlan(data);
    } catch (e) {
      setPlan(null);
      setError(formatApiError(e.response?.data?.detail) || "Could not load route");
    } finally { setLoading(false); }
  }, [operative?.id]);

  useEffect(() => { if (open && date) load(date); }, [open, date, load]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="operative-route-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapTrifold className="h-5 w-5 text-blue-600" weight="duotone" /> Day Route — {operative?.name}
          </DialogTitle>
          <DialogDescription>
            The optimised order from home, round every booked job, and back — as the operative's Route Planner would build it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="w-44">
            <Label className="text-xs">Day</Label>
            <DateInput value={date || ""} onChange={(e) => setDate(e.target.value)} data-testid="route-dialog-date" />
          </div>

          {loading ? (
            <p className="text-sm text-slate-400 py-6 text-center">Planning route…</p>
          ) : error ? (
            <p className="text-sm text-rose-600 py-4 text-center">{error}</p>
          ) : !plan ? null : plan.no_home ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs text-amber-800 dark:text-amber-200" data-testid="route-dialog-no-home">
              <Warning className="h-3.5 w-3.5 inline mr-1" /> {plan.message}
            </div>
          ) : !plan.stops?.length ? (
            <p className="text-sm text-slate-500 py-4 text-center" data-testid="route-dialog-empty">
              {plan.message || "No routable jobs booked for this day."}
            </p>
          ) : (
            <>
              <div className="flex items-center gap-3 text-xs text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/40 rounded-md px-3 py-2">
                <span className="font-semibold">{plan.totals.distance_miles} mi</span>
                <span>{fmtDrive(plan.totals.drive_minutes)} driving</span>
                <span>{plan.totals.stops} stops</span>
                <span className="text-slate-400">{plan.depart_home} → {plan.arrive_home}</span>
              </div>

              {plan.warnings?.length > 0 && (
                <div className="rounded-md border border-rose-300 bg-rose-50 dark:bg-rose-950/30 p-2.5 text-[11px] text-rose-800 dark:text-rose-200">
                  {plan.warnings.map((w, i) => <p key={i}>⚠ {w}</p>)}
                </div>
              )}

              <div className="space-y-0">
                <div className="flex items-center gap-2 py-1.5">
                  <div className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0">
                    <House className="h-3 w-3" weight="fill" />
                  </div>
                  <p className="text-xs"><span className="font-semibold">Leave home {plan.depart_home}</span>
                    <span className="text-slate-400 ml-1.5 truncate">{plan.home.address}</span></p>
                </div>
                {plan.stops.map((s) => (
                  <div key={s.key} className="flex gap-2 py-1.5 border-t border-border" data-testid={`route-dialog-stop-${s.key}`}>
                    <div className="w-6 h-6 rounded-full bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center shrink-0">{s.order}</div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs">
                        <span className="text-blue-600 font-bold mr-1.5">ETA {s.eta}</span>
                        <span className="font-medium text-slate-800 dark:text-slate-200">{s.title}</span>
                        {s.fixed && s.scheduled_time && (
                          <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">Booked {s.scheduled_time}</span>
                        )}
                        {s.late_minutes > 0 && (
                          <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-200">~{s.late_minutes}m late</span>
                        )}
                      </p>
                      <p className="text-[11px] text-slate-500 truncate">
                        <MapPin className="h-3 w-3 inline mr-0.5" />{s.address}
                        <span className="ml-2 text-slate-400"><CarSimple className="h-3 w-3 inline mr-0.5" />{s.leg_distance_miles} mi · {s.leg_duration_minutes}m</span>
                      </p>
                    </div>
                  </div>
                ))}
                <div className="flex items-center gap-2 py-1.5 border-t border-border">
                  <div className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0">
                    <House className="h-3 w-3" weight="fill" />
                  </div>
                  <p className="text-xs font-semibold">Back home ~{plan.arrive_home}
                    <span className="text-slate-400 font-normal ml-1.5"><CarSimple className="h-3 w-3 inline mr-0.5" />{plan.return_leg.distance_miles} mi · {plan.return_leg.duration_minutes}m</span>
                  </p>
                </div>
              </div>

              <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => window.open(plan.google_maps_url, "_blank", "noopener")}
                      data-testid="route-dialog-gmaps-btn">
                <NavigationArrow className="h-4 w-4 mr-2" weight="fill" /> Open route in Google Maps
              </Button>
              {plan.estimated && (
                <p className="text-[11px] text-slate-400 text-center">Drive times are straight-line estimates (live road data unavailable).</p>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
